import { Component, OnInit } from '@angular/core';
import { PasswordResetTokenDto, PasswordResetServiceServiceProxy, PasswordResetRequestDto, SecurityQuestion, UserSecurityQuestionAnswerServiceServiceProxy } from '@shared/service-proxies/service-proxies';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { AlertController, LoadingController } from '@ionic/angular';
import { Router, ActivatedRoute } from '@angular/router';
import * as moment from 'moment';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-password-reset',
  templateUrl: './password-reset.page.html',
  styleUrls: ['./password-reset.page.scss'],
})
export class PasswordResetPage implements OnInit {

  token: string;
  securityQuestion: SecurityQuestion;
  passwordResetToken: PasswordResetTokenDto;
  passwordType: string = 'password';
  passwordIcon: string = 'eye-off';
  passwordConfirmType: string = 'password';
  passwordConfirmIcon: string = 'eye-off';
  formGroup: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private router: Router,
    private route: ActivatedRoute,
    private passwordResetSvc: PasswordResetServiceServiceProxy,
    private titleService: Title,
    private userSecurityQuestionAnswerSvc: UserSecurityQuestionAnswerServiceServiceProxy
  ) {
    this.route.queryParams.subscribe(params => {
      if (params && params.token) {
        this.token = params.token;
        if (this.token) {
          this.getPasswordResetToken(params.token);
        } else {
          this.initializeNewPasswordReset();
        }
      } else {
        this.initializeNewPasswordReset();
      }
      this.setupForm();
    });
    this.titleService.setTitle('OACI - Password Reset');
  }

  setupForm() {
    if (this.formGroup) {
      this.formGroup.reset();
    }
    this.formGroup = this.formBuilder.group({
      emailAddress: [''/*, this.token ? null : Validators.required*/],
      userName: ['', this.token ? null : Validators.required],
      password: ['', this.token ? Validators.required : null],
      passwordConfirm: ['', this.token ? Validators.required : null],
      securityQuestionAnswer: ['', this.securityQuestion ? Validators.required : null]
    });
  }

  getPasswordResetToken(idOrToken) {
    this.passwordResetSvc.getByToken(idOrToken).subscribe(async passwordResetToken => {
      if (passwordResetToken) {
        this.token = passwordResetToken.token;
        this.passwordResetToken = passwordResetToken;
        this.formGroup.patchValue({
          'emailAddress': this.passwordResetToken.userEmailAddress,
          'userName': this.passwordResetToken.userUserName
        });
        if (
          !this.passwordResetToken.isActive ||
          this.passwordResetToken.expirationDate < moment()
        ) {
          this.formGroup.disable();
            
          const alert = await this.alertController.create({
            header: 'Error',
            message: 'This password reset token is expired and/or in-active.',
            buttons: [
              {
                text: 'Okay',
                handler: () => {
                  this.router.navigate(['/account/login']);
                }
              }
            ]
          });

          await alert.present();
        }
      }
    });
  }

  ngOnInit() {
  }

  ionViewWillEnter() {
    this.setupForm();
  }

  async submitForm() {
    if (!this.securityQuestion) { // default
      if (
        this.formGroup.valid &&
        this.passwordsMatch()
      ) {
        this.resetPassword(true).then();
      } else {
        const errorMessages = [];
        if (!this.formGroup.valid) errorMessages.push('&#183; Missing Form Fields.');
        if (!this.passwordsMatch()) errorMessages.push('&#183; Passwords do not match.');
        const alert = await this.alertController.create({
          header: 'Error',
          // subHeader: 'Error',
          message: errorMessages.join('<br>'),
          buttons: ['OK']
        });
    
        await alert.present();
      }
    } else {
      if (this.formGroup.valid) {
        this.checkSecurityQuestionAnswer().then();
      }
    }
  }

  passwordsMatch() { // stupid chrome autofill puts password value in
    return this.passwordResetToken ? this.formGroup.controls.password.value == this.formGroup.controls.passwordConfirm.value : true;
  }

  async resetPassword(doRedirect: boolean) {
    
    const loading = await this.loadingController.create({
      message: 'Requesting password reset...',
    });
    await loading.present();

    if (!this.token) {
      this.passwordResetSvc.create(
        this.formGroup.controls.userName.value,
        // this.formGroup.controls.emailAddress.value,
        doRedirect
      ).subscribe(async repsonse => {
        if (repsonse > -1) {
          await loading.dismiss();
            
          if (doRedirect) {
            const alert = await this.alertController.create({
              header: 'Success',
              message: 'Please check your email for a password reset link.',
              buttons: [
                {
                  text: 'Okay',
                  handler: () => {
                    this.router.navigate(['/account/login']);
                  }
                }
              ]
            });
  
            await alert.present();
          } else {
            this.getPasswordResetToken(repsonse);
          }
        } else {
          await loading.dismiss();
            
          const alert = await this.alertController.create({
            header: 'Error',
            // subHeader: 'Error',
            message: 'User was not found.',
            buttons: ['OK']
          });
      
          await alert.present();
        }
      });
    } else {
    
      const passwordResetRequest = new PasswordResetRequestDto();
      passwordResetRequest.token = this.token;
      passwordResetRequest.password = this.formGroup.controls.password.value;
      this.passwordResetSvc.resetPassword(passwordResetRequest).subscribe(async response => {
        if (response == true) {
          await loading.dismiss();
            
          const alert = await this.alertController.create({
            header: 'Success',
            message: 'Password has been reset.',
            buttons: [
              {
                text: 'Okay',
                handler: () => {
                  this.router.navigate(['/account/login']);
                }
              }
            ]
          });

          await alert.present();
        } else {
          await loading.dismiss();
            
          const alert = await this.alertController.create({
            header: 'Error',
            // subHeader: 'Error',
            message: 'Error resseting password.',
            buttons: ['OK']
          });
      
          await alert.present();
        }
      });

    }
  }

  async checkSecurityQuestionAnswer() {
    
    const loading = await this.loadingController.create({
      message: 'Authenticating Security Question answer...',
    });
    await loading.present();

    this.userSecurityQuestionAnswerSvc.checkIsAnswerCorrect(
      this.formGroup.controls.userName.value,
      this.securityQuestion.id,
      this.formGroup.controls.securityQuestionAnswer.value
    ).subscribe(async isCorrect => {
      await loading.dismiss();
      if (isCorrect) {
        this.securityQuestion = null;
        this.formGroup.controls.securityQuestionAnswer.clearValidators();
        this.formGroup.controls.securityQuestionAnswer.updateValueAndValidity();
        this.resetPassword(false);
      } else {
            
        const alert = await this.alertController.create({
          header: 'Error',
          // subHeader: 'Error',
          message: 'Answer was incorrect.',
          buttons: ['OK']
        });
    
        await alert.present();
      }
    });

  }

  initializeNewPasswordReset() {
    this.token = null;
    this.passwordResetToken = new PasswordResetTokenDto();
  }

  hideShowPassword() {
    this.passwordType = this.passwordType === 'text' ? 'password' : 'text';
    this.passwordIcon = this.passwordIcon === 'eye-off' ? 'eye' : 'eye-off';
  }

  hideShowPasswordConfirm() {
    this.passwordConfirmType = this.passwordConfirmType === 'text' ? 'password' : 'text';
    this.passwordConfirmIcon = this.passwordConfirmIcon === 'eye-off' ? 'eye' : 'eye-off';
  }

  async resetViaEmail() {
    
    const loading = await this.loadingController.create({
      message: 'Searching for User...',
    });
    await loading.present();
    
    this.passwordResetSvc.canFindUser(this.formGroup.controls.userName.value).subscribe(async didFindUser => {
      if (didFindUser) {
        await loading.dismiss();
          
        this.resetPassword(true).then();
      } else {
        await loading.dismiss();
          
        const alert = await this.alertController.create({
          header: 'Error',
          // subHeader: 'Error',
          message: 'User was not found.',
          buttons: ['OK']
        });
    
        await alert.present();
      }
    });
  }

  async resetViaSecurityQuestion() {
    
    const loading1 = await this.loadingController.create({
      message: 'Searching for User...',
    });
    await loading1.present();
    
    this.passwordResetSvc.canFindUser(this.formGroup.controls.userName.value).subscribe(async didFindUser => {
      if (didFindUser) {
        await loading1.dismiss();
    
        const loading2 = await this.loadingController.create({
          message: 'Getting Security Question...',
        });
        await loading2.present();

        this.userSecurityQuestionAnswerSvc.getSecurityQuestion(this.formGroup.controls.userName.value).subscribe(async securityQuestion => {
          await loading2.dismiss();

          if (securityQuestion) {
            this.securityQuestion = securityQuestion;
            this.formGroup.controls.securityQuestionAnswer.setValidators([Validators.required]);
            this.formGroup.controls.securityQuestionAnswer.updateValueAndValidity();
          } else {
            const alert = await this.alertController.create({
              header: 'Error',
              // subHeader: 'Error',
              message: 'No Security Question found for User. Please Reset Password via Email.',
              buttons: ['OK']
            });
        
            await alert.present();
          }
        })
      } else {
        await loading1.dismiss();
          
        const alert = await this.alertController.create({
          header: 'Error',
          // subHeader: 'Error',
          message: 'User was not found.',
          buttons: ['OK']
        });
    
        await alert.present();
      }
    });

  }

}
